import Papa from 'papaparse';

// Helper to get data file URL from backend
const getDataUrl = (path) => {
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
  const url = `${API_BASE}/data/${path}`;
  console.log('🔗 Building data URL:', url);
  return url;
};

export const readCsvFile = async (filePath) => {
  try {
    console.log('📄 Fetching CSV file:', filePath);
    const response = await fetch(filePath);
    console.log('📄 CSV Response status:', response.status, 'OK:', response.ok);
    const text = await response.text();
    
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',');
    
    const data = lines.slice(1).map(line => {
      const values = line.split(',');
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });
      return row;
    });
    
    return data;
  } catch (error) {
    console.error('Error reading CSV file:', error);
    return [];
  }
};

export const getTodaysSTOs = async () => {
  const stoData = await readCsvFile(getDataUrl('synthetic/sto_details.csv'));
  const today = new Date().toISOString().split('T')[0];
  
  return stoData.filter(sto => sto.STO_DATE === today);
};

export const getAllSTOs = async () => {
  return await readCsvFile(getDataUrl('synthetic/sto_details.csv'));
};

export const getSTODetails = async (stoNo) => {
  const stoData = await readCsvFile(getDataUrl('synthetic/sto_details.csv'));
  const stoLines = stoData.filter(row => row.STO_NO === stoNo);
  
  if (stoLines.length === 0) return null;
  
  const totalLines = stoLines.length;
  const totalUnits = stoLines.reduce((sum, row) => sum + parseInt(row.QUANTITY || 0), 0);
  const receivingPlant = stoLines[0].RECEIVING_PLANT;
  
  return {
    stoNo,
    receivingPlant,
    totalLines,
    totalUnits,
    items: stoLines
  };
};

export const getOBDDetails = async (deliveryNo) => {
  const obdData = await readCsvFile(getDataUrl('synthetic/obd_details.csv'));
  const obdLines = obdData.filter(row => row.DELIVERY_NO === deliveryNo);
  
  if (obdLines.length === 0) return null;
  
  const totalLines = obdLines.length;
  const totalUnits = obdLines.reduce((sum, row) => sum + parseInt(row.QUANTITY || 0), 0);
  
  return {
    deliveryNo,
    totalLines,
    totalUnits,
    items: obdLines
  };
};

export const getHandlingUnitsForDelivery = async (deliveryNo) => {
  const huData = await readCsvFile(getDataUrl('synthetic/handling_units.csv'));
  return huData.filter(row => row.DELIVERY_NO === deliveryNo);
};

export const getHandlingUnitDetails = async (huLpn) => {
  const huData = await readCsvFile(getDataUrl('synthetic/handling_units.csv'));
  const items = huData.filter(row => row.HU_LPN === huLpn);
  
  if (items.length === 0) return null;
  
  return {
    huLpn,
    deliveryNo: items[0].DELIVERY_NO,
    items: items.map(item => ({
      material: item.MATERIAL,
      quantity: parseInt(item.QUANTITY || 0)
    }))
  };
};

export const getInventoryForStore = async (plant) => {
  const inventoryData = await readCsvFile(getDataUrl('synthetic/inventory.csv'));
  return inventoryData.filter(row => row.PLANT === plant);
};

export const getSalesHistory = async (plant, daysBack = 30) => {
  const salesData = await readCsvFile(getDataUrl('synthetic/sales_history.csv'));
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysBack);
  const cutoffStr = cutoffDate.toISOString().split('T')[0];
  
  return salesData.filter(row => 
    row.PLANT === plant && row.ORDER_DATE >= cutoffStr
  );
};

export const getReplenishmentParams = async (material, plant) => {
  const replenData = await readCsvFile(getDataUrl('synthetic/replenishment.csv'));
  return replenData.find(row => 
    row.MATERIAL === material && row.PLANT === plant
  );
};

export const getAvailableDates = async () => {
  try {
    const obdData = await readCsvFile(getDataUrl('synthetic/obd_details.csv'));
    const dates = [...new Set(obdData.map(row => row.DELIV_DATE))].sort();
    return dates;
  } catch (error) {
    console.error('Error loading available dates:', error);
    return [];
  }
};

export const getOBDsForDate = async (targetDate) => {
  try {
    const obdData = await readCsvFile(getDataUrl('synthetic/obd_details.csv'));
    
    // Filter for specified date
    const dateDeliveries = obdData.filter(row => row.DELIV_DATE === targetDate);
    
    // Group by delivery number
    const obdMap = new Map();
    dateDeliveries.forEach(row => {
      if (!obdMap.has(row.DELIVERY_NO)) {
        obdMap.set(row.DELIVERY_NO, {
          deliveryNo: row.DELIVERY_NO,
          date: row.DELIV_DATE,
          lines: 0,
          units: 0,
          items: []
        });
      }
      const obd = obdMap.get(row.DELIVERY_NO);
      obd.lines++;
      obd.units += parseInt(row.QUANTITY || 0);
      obd.items.push({
        material: row.MATERIAL,
        quantity: parseInt(row.QUANTITY || 0)
      });
    });
    
    return Array.from(obdMap.values());
  } catch (error) {
    console.error('Error loading OBDs for date:', error);
    return [];
  }
};

export const getTodaysOBDs = async () => {
  try {
    const obdData = await readCsvFile(getDataUrl('synthetic/obd_details.csv'));
    
    // Get the first date from synthetic data (since 'today' might not exist)
    const dates = [...new Set(obdData.map(row => row.DELIV_DATE))].sort();
    const targetDate = dates[0] || new Date().toISOString().split('T')[0];
    
    console.log('Using date for OBDs:', targetDate);
    
    // Filter for specified date
    const todaysDeliveries = obdData.filter(row => row.DELIV_DATE === targetDate);
    
    // Group by delivery number
    const obdMap = new Map();
    todaysDeliveries.forEach(row => {
      if (!obdMap.has(row.DELIVERY_NO)) {
        obdMap.set(row.DELIVERY_NO, {
          deliveryNo: row.DELIVERY_NO,
          date: row.DELIV_DATE,
          lines: 0,
          units: 0,
          items: []
        });
      }
      const obd = obdMap.get(row.DELIVERY_NO);
      obd.lines++;
      obd.units += parseInt(row.QUANTITY || 0);
      obd.items.push({
        material: row.MATERIAL,
        quantity: parseInt(row.QUANTITY || 0)
      });
    });
    
    return Array.from(obdMap.values());
  } catch (error) {
    console.error('Error loading today\'s OBDs:', error);
    return [];
  }
};

export const getAllOBDsInDateRange = async (startDate, endDate) => {
  try {
    const obdData = await readCsvFile(getDataUrl('synthetic/obd_details.csv'));
    
    const filteredDeliveries = obdData.filter(row => 
      row.DELIV_DATE >= startDate && row.DELIV_DATE <= endDate
    );
    
    const obdMap = new Map();
    filteredDeliveries.forEach(row => {
      if (!obdMap.has(row.DELIVERY_NO)) {
        obdMap.set(row.DELIVERY_NO, {
          deliveryNo: row.DELIVERY_NO,
          date: row.DELIV_DATE,
          lines: 0,
          units: 0
        });
      }
      const obd = obdMap.get(row.DELIVERY_NO);
      obd.lines++;
      obd.units += parseInt(row.QUANTITY || 0);
    });
    
    return Array.from(obdMap.values());
  } catch (error) {
    console.error('Error loading OBDs:', error);
    return [];
  }
};

// Configuration Data Functions

// Get dispatch times from localStorage
export const getDispatchTimes = () => {
  const savedTimes = localStorage.getItem('dispatchTimes');
  if (savedTimes) {
    return JSON.parse(savedTimes);
  }
  
  // Default dispatch times
  const platforms = ['ASH', 'ATL', 'BNA', 'CAE', 'GSO', 'MEM', 'PNS', 'RDU', 'SAV', 'TYS'];
  const defaultTimes = {};
  platforms.forEach(platform => {
    defaultTimes[platform] = '5:00 PM';
  });
  return defaultTimes;
};

// Get delivery schedule from localStorage  
export const getDeliverySchedule = () => {
  const savedSchedule = localStorage.getItem('deliverySchedule');
  if (savedSchedule) {
    return JSON.parse(savedSchedule);
  }
  return {};
};

// Get delivery cost configuration from localStorage
export const getDeliveryCostConfig = () => {
  const savedConfig = localStorage.getItem('deliveryCostConfig');
  if (savedConfig) {
    return JSON.parse(savedConfig);
  }
  
  // Default config
  return {
    warehouseWageRate: 20.00,
    platformWageRate: 18.00,
    platformSettings: {}
  };
};

// Get backroom space configuration from localStorage
export const getBackroomSpaceConfig = () => {
  const savedSpace = localStorage.getItem('backroomSpace');
  if (savedSpace) {
    return JSON.parse(savedSpace);
  }
  return {};
};

// Helper functions for delivery optimizer
export const getBackroomSpace = (platform, storeNumber) => {
  const backroomData = getBackroomSpaceConfig();
  const key = `${platform}-${storeNumber}`;
  return backroomData[key] || null;
};

export const getAllBackroomData = () => {
  return getBackroomSpaceConfig();
};

// Transform OBD data for chart consumption
export const transformOBDDataForCharts = (obdData) => {
  console.log('=== TRANSFORMING OBD DATA FOR CHARTS ===');
  console.log('Input OBD data length:', obdData.length);
  
  // 1. Order Trends Over Time
  const trendData = obdData.reduce((acc, item) => {
    const date = item.loadDate;
    if (!date) return acc;
    
    if (!acc[date]) {
      acc[date] = { date, orders: 0, qtyOrdered: 0, qtyPicked: 0 };
    }
    acc[date].orders++;
    acc[date].qtyOrdered += item.qtyOrdered || 0;
    acc[date].qtyPicked += item.qtyPicked || 0;
    return acc;
  }, {});
  
  const orderTrends = Object.values(trendData)
    .map(d => ({
      date: d.date || 'Unknown',
      orders: d.orders || 0,
      qtyOrdered: d.qtyOrdered || 0,
      qtyPicked: d.qtyPicked || 0
    }))
    .filter(d => d.orders > 0)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // 2. Top Stores by Order Volume  
  const storeStats = obdData.reduce((acc, item) => {
    const store = item.plant || 'Unknown';
    if (!acc[store]) {
      acc[store] = { 
        store, 
        name: `Store ${store}`,
        platform: item.deployment || 'Unknown',
        orders: 0, 
        qtyOrdered: 0,
        qtyPicked: 0,
        fillRate: 0
      };
    }
    acc[store].orders++;
    acc[store].qtyOrdered += item.qtyOrdered || 0;
    acc[store].qtyPicked += item.qtyPicked || 0;
    return acc;
  }, {});
  
  // Calculate fill rates and sort with null safety
  const topStores = Object.values(storeStats).map(store => ({
    store: store.store || 'Unknown',
    name: store.name || `Store ${store.store || 'Unknown'}`,
    platform: store.platform || 'Unknown', 
    orders: store.orders || 0,
    qtyOrdered: store.qtyOrdered || 0,
    qtyPicked: store.qtyPicked || 0,
    fillRate: store.qtyOrdered > 0 ? (store.qtyPicked / store.qtyOrdered) * 100 : 0
  })).filter(s => s.orders > 0).sort((a, b) => b.orders - a.orders);
  
  // 3. Material Groups Performance
  const materialGroupStats = obdData.reduce((acc, item) => {
    const group = item.materialGroupName || item.materialGroup || 'Unknown';
    if (!acc[group]) {
      acc[group] = { 
        name: group,
        orders: 0, 
        qtyOrdered: 0, 
        qtyPicked: 0,
        fillRate: 0
      };
    }
    acc[group].orders++;
    acc[group].qtyOrdered += item.qtyOrdered || 0;
    acc[group].qtyPicked += item.qtyPicked || 0;
    return acc;
  }, {});
  
  const materialGroups = Object.values(materialGroupStats).map(group => ({
    name: group.name || 'Unknown',
    orders: group.orders || 0,
    qtyOrdered: group.qtyOrdered || 0,
    qtyPicked: group.qtyPicked || 0,
    fillRate: group.qtyOrdered > 0 ? (group.qtyPicked / group.qtyOrdered) * 100 : 0
  })).filter(g => g.orders > 0).sort((a, b) => b.orders - a.orders);
  
  // 4. Daily Quantity Picked (for heatmap)
  const dailyQuantity = obdData.reduce((acc, item) => {
    const date = item.loadDate;
    if (!date) return acc;
    
    if (!acc[date]) {
      acc[date] = { date, totalQty: 0 };
    }
    acc[date].totalQty += item.qtyPicked || 0;
    return acc;
  }, {});
  
  const dailyQuantityData = Object.values(dailyQuantity)
    .map(d => ({
      date: d.date || 'Unknown',
      totalQty: d.totalQty || 0
    }))
    .filter(d => d.totalQty > 0)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  console.log('Chart data prepared:', {
    orderTrends: orderTrends.length,
    topStores: topStores.length,
    materialGroups: materialGroups.length,
    dailyQuantity: dailyQuantityData.length
  });
  
  return {
    orderTrends,
    topStores,
    materialGroups,
    dailyQuantityData
  };
};

// Read OBD historical data file (CSV version)
export const readOBDHistoricalData = async () => {
  console.log("=== LOADING OBD HISTORICAL CSV ===");
  
  try {
    const dataUrl = getDataUrl('OBD_REPORT_HISTORY-10_10.csv');
    console.log('Attempting to fetch from backend:', dataUrl);
    const response = await fetch(dataUrl);
    console.log('Fetch response status:', response.status, 'OK:', response.ok);
    console.log('Fetch response ok:', response.ok);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const text = await response.text();
    console.log("CSV file size:", text.length, "characters");
    console.log("First 500 chars:", text.substring(0, 500));
    
    console.log('=== PARSING CSV WITH PAPAPARSE ===');
    const result = Papa.parse(text, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header) => {
        console.log(`Header: "${header}"`);
        return header;
      }
    });
    
    console.log("Parse errors:", result.errors);
    console.log("Parsed rows:", result.data.length);
    console.log("Headers found:", result.meta.fields);
    console.log("Sample parsed row:", result.data[0]);
    
    // Map CSV columns to our format based on the CSV structure
    console.log('=== MAPPING CSV DATA ===');
    const historicalData = result.data.map((row, index) => {
      // Based on CSV headers: LOADDATE, Delivery, Material, Item Description, Delivery Quantity (Ordered), Delivery Quantity (Picked)
      const qtyOrdered = parseFloat(row['Delivery Quantity (Ordered)'] || 0);
      const qtyPicked = parseFloat(row['Delivery Quantity (Picked)'] || 0);
      const shortQty = qtyOrdered - qtyPicked;
      const fulfillmentRate = qtyOrdered > 0 ? qtyPicked / qtyOrdered : 0;
      
      const mapped = {
        loadDate: row['LOADDATE'] || '',
        obdNumber: row['Delivery'] || '',
        material: row['Material'] || '',
        description: row['Item Description'] || '',
        deployment: row['Deployment'] || '', // Platform information
        plant: row['Plant'] || '', // Store information
        materialGroup: row['Material Group'] || '',
        materialGroupName: row['Material Group Name'] || '',
        qtyOrdered: qtyOrdered,
        qtyPicked: qtyPicked,
        shortQty: shortQty,
        fulfillmentRate: fulfillmentRate,
        source: 'historical'
      };
      
      // Debug first few mapped items
      if (index < 5) {
        console.log(`Mapped historical ${index}:`, {
          originalRow: {
            LOADDATE: row['LOADDATE'],
            Delivery: row['Delivery'],
            Material: row['Material'],
            'Delivery Quantity (Ordered)': row['Delivery Quantity (Ordered)'],
            'Delivery Quantity (Picked)': row['Delivery Quantity (Picked)']
          },
          mapped: {
            obdNumber: mapped.obdNumber,
            material: mapped.material,
            qtyOrdered: mapped.qtyOrdered,
            qtyPicked: mapped.qtyPicked,
            fulfillmentRate: (mapped.fulfillmentRate * 100).toFixed(1) + '%'
          }
        });
      }
      
      return mapped;
    });
    
    // Filter out invalid records
    console.log('=== FILTERING VALIDATION ===');
    console.log('Records before filtering:', historicalData.length);
    
    const validData = historicalData.filter((record, i) => {
      const isValid = record.obdNumber && record.material && record.qtyOrdered > 0;
      
      if (!isValid && i < 10) {
        console.log(`Invalid Row ${i}:`, {
          obdNumber: `"${record.obdNumber}"`,
          material: `"${record.material}"`, 
          qtyOrdered: record.qtyOrdered,
          reasons: [
            !record.obdNumber && 'No OBD number',
            !record.material && 'No material',
            !(record.qtyOrdered > 0) && 'Invalid qty ordered'
          ].filter(Boolean)
        });
      }
      
      return isValid;
    });
    
    console.log(`✅ CSV PARSING SUCCESS: ${historicalData.length} → ${validData.length} valid records`);
    
    // Analyze platforms and stores in historical data
    if (validData.length > 0) {
      const platforms = [...new Set(validData.map(d => d.deployment))].filter(Boolean);
      const stores = [...new Set(validData.map(d => d.plant))].filter(Boolean);
      const materialGroups = [...new Set(validData.map(d => d.materialGroup))].filter(Boolean);
      
      console.log('=== OBD DATA ANALYSIS ===');
      console.log('Platforms in OBD data:', platforms);
      console.log('Stores in OBD data:', stores.length, 'unique stores');
      console.log('Sample stores:', stores.slice(0, 10));
      console.log('Material Groups:', materialGroups.length, 'groups');
      console.log('Sample groups:', materialGroups.slice(0, 5));
      
      console.log('Date range:', {
        earliest: validData[0]?.loadDate,
        latest: validData[validData.length - 1]?.loadDate
      });
    }
    
    return validData;
  } catch (error) {
    console.error("Error loading historical CSV:", error);
    return [];
  }
};

// Merge historical OBD data with existing synthetic data
export const getMergedOBDData = async () => {
  try {
    console.log('=== DETAILED DATA LOADING DEBUG ===');
    
    // Step 1: Load synthetic data
    console.log('Step 1: Loading synthetic data...');
    const syntheticData = await getTodaysOBDs();
    console.log('Synthetic records loaded:', syntheticData.length);
    console.log('Sample synthetic:', syntheticData[0]);
    
    // Step 2: Load historical data  
    console.log('Step 2: Loading historical data...');
    const historicalData = await readOBDHistoricalData();
    console.log('Historical records loaded:', historicalData ? historicalData.length : 0);
    console.log('Sample historical:', historicalData ? historicalData[0] : 'FAILED TO LOAD');
    
    if (!historicalData) {
      console.error('🚨 HISTORICAL DATA FAILED TO LOAD');
      return syntheticData; // Fallback to synthetic only
    }
    
    // Step 3: Convert synthetic data to match historical format
    console.log('Step 3: Converting synthetic data format...');
    const standardizedSynthetic = syntheticData.map(obd => ({
      loadDate: obd.date || new Date().toISOString().split('T')[0],
      obdNumber: obd.deliveryNo || '',
      material: 'N/A', // Synthetic data doesn't have material details
      description: 'Synthetic OBD',
      qtyOrdered: obd.units || 0,
      qtyPicked: Math.floor((obd.units || 0) * 0.98), // Assume 98% fulfillment for synthetic
      shortQty: Math.floor((obd.units || 0) * 0.02),
      fulfillmentRate: 0.98,
      lines: obd.lines || 0,
      source: 'synthetic'
    }));
    console.log('Standardized synthetic data:', standardizedSynthetic.length);
    
    // Step 4: Merge all data sources
    console.log('Step 4: Merging data sources...');
    const allData = [...standardizedSynthetic, ...historicalData];
    console.log('Combined data before dedup:', allData.length);
    
    // Step 5: Remove duplicates
    console.log('Step 5: Removing duplicates...');
    const uniqueOBDs = new Map();
    allData.forEach(obd => {
      const key = obd.obdNumber;
      if (!uniqueOBDs.has(key) || obd.source === 'historical') {
        uniqueOBDs.set(key, obd);
      }
    });
    
    const mergedData = Array.from(uniqueOBDs.values());
    console.log('After deduplication:', mergedData.length);
    
    // Step 6: Sort by date
    console.log('Step 6: Sorting by date...');
    mergedData.sort((a, b) => new Date(b.loadDate) - new Date(a.loadDate));
    
    console.log(`✅ FINAL MERGED DATA: ${syntheticData.length} synthetic + ${historicalData.length} historical = ${mergedData.length} total`);
    console.log('Final data sources breakdown:', {
      synthetic: mergedData.filter(obd => obd.source === 'synthetic').length,
      historical: mergedData.filter(obd => obd.source === 'historical').length
    });
    
    return mergedData;
  } catch (error) {
    console.error('Error merging OBD data:', error);
    return [];
  }
};